/**
 * Pre-submission conflict detection for "Insert Missed Entry"
 * Mirrors backend record_keeper.py Rules 1 & 2
 */

/**
 * Check if inserting a segment of `segType` at the given date/time would conflict
 * with existing segments.
 * 
 * @param {Array} segments - Current segments array
 * @param {string} segType - 'WORK' or 'REST'
 * @param {string} date - YYYY-MM-DD
 * @param {string} startTime - HH:MM
 * @param {string} endTime - HH:MM
 * @returns {string|null} - Conflict message or null if OK
 */
export function checkInsertConflict(segments, segType, date, startTime, endTime) {
  if (!segments || !segments.length || !date || !startTime || !endTime) return null;

  const proposedStart = new Date(`${date}T${startTime}:00`);
  let proposedEnd = new Date(`${date}T${endTime}:00`);
  if (isNaN(proposedStart.getTime()) || isNaN(proposedEnd.getTime())) return null;

  // Handle cross-midnight
  if (proposedEnd <= proposedStart) {
    proposedEnd = new Date(proposedEnd.getTime() + 24 * 60 * 60 * 1000);
  }

  // Find overlapping segments (mirrors backend lines 393-407)
  const overlapping = segments.filter(seg => {
    const segStart = new Date(seg.start_ts);
    const segEnd = seg.end_ts ? new Date(seg.end_ts) : new Date();
    // Ignore far-future sentinels (open-ended segments)
    const now = new Date();
    const isSentinel = segEnd && (segEnd - now) > 30 * 24 * 60 * 60 * 1000;
    const effectiveEnd = isSentinel ? new Date(now.getTime() + 60000) : segEnd;
    return segStart < proposedEnd && effectiveEnd > proposedStart;
  });

  // Rule 1: Cannot insert same type INTO same type
  const sameType = overlapping.find(seg => seg.segment_type === segType);
  if (sameType) {
    return `Cannot insert ${segType} here — it overlaps with an existing ${sameType.segment_type} segment. Insert the opposite type instead.`;
  }

  // Rule 2: Gap check — if no overlapping segments, check adjacent types
  if (overlapping.length === 0) {
    const sorted = [...segments].sort((a, b) => new Date(a.start_ts) - new Date(b.start_ts));
    let segBefore = null;
    let segAfter = null;
    for (const s of sorted) {
      const sEnd = s.end_ts ? new Date(s.end_ts) : new Date();
      if (sEnd <= proposedStart) segBefore = s;
      if (new Date(s.start_ts) >= proposedEnd && !segAfter) segAfter = s;
    }

    if (segBefore && segBefore.segment_type === segType) {
      return `Cannot insert ${segType} here — the previous segment is also ${segType}. Diary must alternate WORK → REST → WORK.`;
    }
    if (segAfter && segAfter.segment_type === segType) {
      return `Cannot insert ${segType} here — the next segment is also ${segType}. Diary must alternate WORK → REST → WORK.`;
    }
  }

  return null;
}
